import React, {useState, useEffect, useContext, useMemo, Fragment} from 'react';
import {
  MapContainer,
  useMapEvents,
  TileLayer,
  Marker,
  useMap,
  Polygon,
  Polyline,
  CircleMarker,
  Rectangle,
  Popup, Circle
} from 'react-leaflet';
import MapViewMarker from './MapViewMarker';
import MapViewMarkerPending from './MapViewMarkerPending';
import MapViewMarkerCompleted from './MapViewMarkerCompleted';
import MapViewMarkerCancelled from './MapViewMarkerCancelled';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import './MapView.scss';
import {useSelector} from "react-redux";
import AntennaMarker from "./AntennaMarker";


const selector = state => {
  const { language } = state;
  return {
    t: language.translator
  }
};

const MapView = (
  {
    zoom,
    center,
    markers,
    antennasMarkers,
    locationMarker,
    getCoordinates,
    pendingMarkers,
    completedMarkers,
    cancelledMarkers,
    area,
    drawingInProgress,
    finishDrawing,
    areas,
    selectedMarker,
    mapBounds,
    edit,
    mapHasBeenClicked,
    popupMarkers,
    popupContainer,
    avoidResize,
    satellite,
    centeredMapOnSensor,
    centerOnLocationMarker,
    onMarkerPopupClick
  }
) => {
  const { t } = useSelector(selector);
  const [locationMarkerCoordinates, setLocationMarkerCoordinates] = useState(locationMarker);
  const [centerCoordinates, setCenterCoordinates] = useState(center);
  const [zoomScale, setZoomScale] = useState(zoom);
  const [mouseCoordinates, setMouseCoordinates] = useState(null);

  const MapEvents = () => {
    const map = useMapEvents({
      click: e => {
        const { originalEvent } = e;
        if (!originalEvent.target.closest('.leaflet-marker-icon')) {
          if (getCoordinates) {
            setLocationMarkerCoordinates(e.latlng);
            getCoordinates(e.latlng);
          }
        }
        if (!drawingInProgress) {
          map.setView(e.latlng);
        }
      },
      mousemove: e => {
        if (drawingInProgress && area?.length) {
          setMouseCoordinates(e.latlng);
        }
      }
    });
    return null;
  };



  const ChangeView = ({center, selectedMarker, zoom, centerOnLocationMarker}) => {
    const map = useMap();

    if (edit && !drawingInProgress && area && area.length) {
      map.setView(area.at(-1));
      map.setZoom(11);
    }

    if (!selectedMarker && mapBounds) {
      map.fitBounds(mapBounds);
    }

    if (selectedMarker) {
      map.setZoom(zoom);
      setTimeout(() => {
        map.setView(selectedMarker);
      }, 100);
    }

    useEffect(() => {
      if (centerOnLocationMarker && center) {
        map.setView(center, map.getZoom());
      }
    }, [centerOnLocationMarker, center, map]);
    //
    // useEffect(() => {
    //   if (centeredMapOnSensor && (!map.getCenter().equals(centeredMapOnSensor))) {
    //     map.flyTo(centeredMapOnSensor);
    //   }
    // }, [centeredMapOnSensor, map]);

    return null;
  }

  const ExpandMap = () => {
    const map = useMap();
    setTimeout(() => {
      map.invalidateSize(true);
    }, 100);
    return null;
  }

  useEffect(() => {
    setLocationMarkerCoordinates(locationMarker);
    setCenterCoordinates(locationMarker);
  }, [locationMarker]);

  // Centering map to each selected point while drawing
  //
  // useEffect(() => {
  //   if (area && area.length) {
  //     const newCenter = [area[area.length - 1].lat, area[area.length - 1].lon];
  //     setLocationMarkerCoordinates(newCenter);
  //     setCenterCoordinates(newCenter);
  //   }
  // }, [area]);

  useEffect(() => {
    setLocationMarkerCoordinates(locationMarker);
    area?.length ? setCenterCoordinates(area.at(-1)) : setCenterCoordinates(locationMarker);
  }, [drawingInProgress]);

  useEffect(() => {
    setZoomScale(zoom);
  }, [zoom]);

  useEffect(() => {
    if (!drawingInProgress) {
      setCenterCoordinates(center);
    }
  }, [center]);

  const setTooltipPosition = e => {
    const tooltip = document.getElementById('drawingTooltip');
    if (tooltip) {
      const bounds = e.target.getBoundingClientRect();
      const x = e.clientX - bounds.left;
      const y = e.clientY - bounds.top;

      tooltip.style.left = `${x + 20}px`;
      tooltip.style.top = `${y - 14}px`;
    }
  };

  // const CenterMapToMarker = ({center}) => {
  //   console.log('przenosze')
  //   const map = useMap();
  //   map.flyTo([center.lat, center.lng], map.getZoom());
  //
  //   return null;
  // };


  return (
    <div
      className={`MapView ${drawingInProgress ? "MapView--drawing" : ""}`}
      onMouseMove={drawingInProgress && !area?.length ? e => setTooltipPosition(e) : null}
    >
      <MapContainer id='map' center={centerCoordinates} zoom={zoomScale} scrollWheelZoom={true} bounds={mapBounds}>
        {satellite ? (
            <ReactLeafletGoogleLayer
                key="satellite"
                apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                type={'satellite'}
            />
        ) : (
            <TileLayer
                key="default"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
        )}
        {markers?.length && (
          markers.map((marker, index) => (
              <Marker key={index} position={marker} icon={MapViewMarker}>
                <Popup
                    direction={"top"}
                    className={'fieldTooltipWrapper'}
                    bubblingMouseEvents={false}
                >
                  <div
                      className="fieldTooltipContainer"
                      onClick={() => onMarkerPopupClick?.(marker)}
                  >
                    <div className="fieldTooltipName">
                      {marker?.name}
                    </div>
                    <div>
                      {t.gettext('Temperature')}: {marker?.temperature}
                    </div>
                    <div>
                      {t.gettext('Humidity')}: {marker?.humidity}
                    </div>
                  </div>
                </Popup>
              </Marker>
          ))
          )}
        {antennasMarkers?.length && (
            antennasMarkers.map((marker, index) => (
                <Fragment key={index}>
                  <Marker position={marker} icon={AntennaMarker}>
                    <Popup
                        direction={"top"}
                        className={'fieldTooltipWrapper'}
                        bubblingMouseEvents={false}
                    >
                      <div
                          className="fieldTooltipContainer"
                      >
                        <div className="fieldTooltipName">
                          {marker?.name}
                        </div>
                        <div>
                          {t.gettext('Gateway Code')}: {marker.gatewayCode ? marker.gatewayCode : '-'}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                  <Circle center={marker} radius={5000} fillColor="#00aed2" fillOpacity={0.2} />
                </Fragment>
            ))
        )}
        {popupMarkers?.length && (
            popupMarkers.map((marker, index) => (
                <Fragment>
                  <Marker key={index} position={marker} icon={MapViewMarker}>
                    <Popup>
                      {popupContainer(marker)}
                    </Popup>
                  </Marker>

                </Fragment>
            ))
        )}
        {cancelledMarkers?.length && (
          cancelledMarkers.map((marker, index) => (
            <Marker key={index} position={marker} icon={MapViewMarkerCancelled} />
          ))
        )}
        {completedMarkers?.length && (
          completedMarkers.map((marker, index) => (
            <Marker key={index} position={marker} icon={MapViewMarkerCompleted} />
          ))
        )}
        {pendingMarkers?.length && (
          pendingMarkers.map((marker, index) => (
            <Marker key={index} position={marker} icon={MapViewMarkerPending} />
          ))
        )}
        {!!mapHasBeenClicked && !!locationMarkerCoordinates && !area && (
            <Marker position={locationMarkerCoordinates} icon={MapViewMarkerPending} />
        )}
        {!!locationMarkerCoordinates && !area && (
          <Marker position={locationMarkerCoordinates} icon={MapViewMarker} />
        )}
        {!!drawingInProgress && !!area && !!area.length && !!mouseCoordinates && (
          <Polyline className='mapDrawLine' positions={[area.at(-1), mouseCoordinates]} />
        )}
        {!!areas && !!areas.length &&
          areas.map((fieldArea, index) => <Polygon className='mapPolygon' positions={fieldArea} key={index}/>)
        }
        {!!area && !!area.length && (
          <Polyline className='mapPolygon' positions={area} />
        )}
        {!drawingInProgress && !!area && !!area.length && (
          <Polygon className='mapPolygon' positions={area} />
        )}
        {!!area && area.length && (
          area.map((point, index) => (
            <CircleMarker
              className='mapCircleMarker'
              center={point}
              radius={4}
              key={index}
            />
          ))
        )}
        {!!drawingInProgress && !!area && !!area.length && area.length > 2 && (
          <Marker
            position={area[0]}
            icon={MapViewMarker}
            eventHandlers={{
              click: e => {
                if (area.length > 2) {
                  finishDrawing();
                  // setCenterCoordinates(area[0]);
                  // setLocationMarkerCoordinates(area[0]);
                  setZoomScale(1);
                }
              }
            }}
          />
        )}
        {!avoidResize && (
            <ExpandMap />
        )}
        <MapEvents />
        <ChangeView
          center={centerCoordinates ? centerCoordinates : center}
          selectedMarker={selectedMarker}
          zoom={zoom ? zoomScale : zoom}
          centerOnLocationMarker={centerOnLocationMarker}
        />
        {/*{center && (*/}
        {/*    <CenterMapToMarker center={center} />*/}
        {/*)}*/}
      </MapContainer>
      {!!drawingInProgress && !!area && !area.length && (
        <div id='drawingTooltip' className='drawingTooltip'>{t('click to start drawing shape')}.</div>
      )}
      {!!drawingInProgress && !!area && area.length && area.length > 2 && (
        <div className='drawingClosingTooltip'>{t('click on the marker of the first selection point to finish drawing')}.</div>
      )}
    </div>
  );
}

export default MapView;
